import type { SubcategoryQueryFilters } from '@/app/lib/api/api'
import { readSearchParam, type SearchParamsInput } from '@/app/lib/utils'

/**
 * Mapa campo→parámetro legible de la URL (sin sufijos técnicos). Única fuente de
 * verdad compartida entre el parseo server-side (`page.tsx`) y la actualización de
 * la URL en cliente (`useListingFieldFilters`, inyectado desde `SubcategoriaFilters`).
 * La provincia NO está aquí: viaja en el segmento `[location]` de la ruta, no como
 * query param (FR-018). Precio pasó de un `min_price` de tramo único a un rango
 * continuo (revisado — mismo mecanismo que Promociones): `priceMin`/`priceMax`
 * viajan como `price_min`/`price_max`; `freeFilter` (checkbox "Mostrar solo
 * gratuitos") como `free_filter`.
 */
export const READABLE_FILTER_PARAMS = {
  sort: 'sort',
  dateRange: 'date_range',
  priceMin: 'price_min',
  priceMax: 'price_max',
  freeFilter: 'free_filter',
} as const

function validatePriceValue(raw: string | undefined): number | undefined {
  if (!raw) return undefined
  const value = Number(raw)
  return Number.isNaN(value) ? undefined : value
}

/**
 * `searchParams` de Next.js (query legible de la URL) → filtros reales que espera
 * `getSubcategoryPage`. Los parámetros ausentes o vacíos se omiten (FR-014a: sin
 * `sort` no se envía orden; el orden por defecto lo decide el backend).
 */
export function parseSubcategoriaFilters(searchParams: SearchParamsInput): SubcategoryQueryFilters {
  const sort = readSearchParam(searchParams, READABLE_FILTER_PARAMS.sort)
  const dateRange = readSearchParam(searchParams, READABLE_FILTER_PARAMS.dateRange)
  const priceMin = validatePriceValue(readSearchParam(searchParams, READABLE_FILTER_PARAMS.priceMin))
  const priceMax = validatePriceValue(readSearchParam(searchParams, READABLE_FILTER_PARAMS.priceMax))
  const freeFilter = readSearchParam(searchParams, READABLE_FILTER_PARAMS.freeFilter) === 'on'

  const filters: SubcategoryQueryFilters = {}
  if (sort) filters.sort = sort
  if (dateRange) filters.dateRange = dateRange
  if (priceMin !== undefined) filters.priceMin = priceMin
  if (priceMax !== undefined) filters.priceMax = priceMax
  if (freeFilter) filters.freeFilter = true
  return filters
}
